import { useState } from "react";
import { DEPARTMENTS } from "../../config/api";
import { phoneDigits } from "../../utils/phoneFormat";
import PhoneInput from "./PhoneInput";

const inputClass =
    "w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#cda834]/50 focus:border-[#cda834] transition-all";

const passwordBytes = (value) => encodeURIComponent(String(value || ""))
    .replace(/%[0-9A-F]{2}/gi, "x").length;

/**
 * Manual HR employee create (POST /employees) — not ERP panel users.
 */
export default function HiredEmployeeForm({ onClose, onSubmit, isLoading = false }) {
    const [form, setForm] = useState({
        full_name: "",
        department: DEPARTMENTS[0] || "",
        position: "",
        phone_number: "",
        email: "",
        telegram_username: "",
        birth_date: "",
        create_birthday_events: true,
        date_hired: new Date().toISOString().slice(0, 10),
        gender: "",
        service_length: "0",
        adaptation_route: "",
        create_erp_account: true,
        erp_role: "dev",
        erp_password: "",
    });
    const [error, setError] = useState("");

    const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        const fullName = form.full_name.trim();
        const position = form.position.trim();
        const department = String(form.department || "").trim();
        if (!fullName || !position || !department) {
            setError("Заполните ФИО, отдел и должность.");
            return;
        }
        if (form.create_erp_account && (!form.email.trim() || !form.email.includes("@"))) {
            setError("Для учётной записи ERP укажите корректный email.");
            return;
        }
        if (form.create_erp_account && form.phone_number.trim() && !phoneDigits(form.phone_number)) {
            setError("Для ERP укажите российский номер: +7 900 000-00-00.");
            return;
        }
        if (form.create_erp_account && passwordBytes(form.erp_password) > 72) {
            setError("Пароль ERP не должен превышать 72 байта.");
            return;
        }
        const payload = {
            full_name: fullName,
            department,
            position,
            date_hired: form.date_hired || null,
            phone_number: form.phone_number.trim() || null,
            email: form.email.trim().toLowerCase() || null,
            telegram_username: form.telegram_username.trim() || null,
            birth_date: form.birth_date || null,
            create_birthday_events: Boolean(form.birth_date && form.create_birthday_events),
            service_length: Math.max(0, Number(form.service_length) || 0),
            create_erp_account: form.create_erp_account,
        };
        if (form.create_erp_account) {
            payload.erp_role = form.erp_role;
            payload.erp_password = form.erp_password || null;
        }
        if (form.gender === "male" || form.gender === "female") {
            payload.gender = form.gender;
        }
        if (form.adaptation_route) {
            payload.adaptation_route = form.adaptation_route;
        }
        try {
            await onSubmit(payload);
        } catch (err) {
            setError(err?.message || String(err));
        }
    };

    return (
        <div
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={onClose}
            data-testid="hired-employee-form-overlay"
        >
            <form
                onSubmit={handleSubmit}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto p-6 md:p-8"
                data-testid="hired-employee-form"
            >
                <div className="flex justify-between items-start gap-4 mb-5">
                    <div>
                        <h2 className="text-2xl font-bold text-slate-900">Новый сотрудник</h2>
                        <p className="text-sm text-slate-500 mt-1">
                            Одна форма создаёт карточку HR и, при необходимости, связанную учётку ERP.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-700 text-2xl leading-none"
                        aria-label="Закрыть"
                    >
                        ×
                    </button>
                </div>

                <div className="space-y-4">
                    <label className="block text-sm">
                        <span className="font-semibold text-slate-700">ФИО *</span>
                        <input
                            className={`${inputClass} mt-1.5`}
                            value={form.full_name}
                            onChange={(e) => setField("full_name", e.target.value)}
                            placeholder="Иванов Иван Иванович"
                            required
                            data-testid="employee-full-name"
                        />
                    </label>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <label className="block text-sm">
                            <span className="font-semibold text-slate-700">Отдел *</span>
                            <select
                                className={`${inputClass} mt-1.5`}
                                value={form.department}
                                onChange={(e) => setField("department", e.target.value)}
                                required
                                data-testid="employee-department"
                            >
                                {DEPARTMENTS.map((d) => (
                                    <option key={d} value={d}>
                                        {d}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="block text-sm">
                            <span className="font-semibold text-slate-700">Должность *</span>
                            <input
                                className={`${inputClass} mt-1.5`}
                                value={form.position}
                                onChange={(e) => setField("position", e.target.value)}
                                placeholder="Логист"
                                required
                                data-testid="employee-position"
                            />
                        </label>
                    </div>

                    <section className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5" data-testid="erp-account-section">
                        <label className="flex cursor-pointer items-start gap-3">
                            <input type="checkbox" className="mt-1 h-4 w-4 accent-emerald-600" checked={form.create_erp_account} onChange={(e) => setField("create_erp_account", e.target.checked)} data-testid="employee-create-erp-account" />
                            <span><strong className="block text-sm text-slate-900">Создать учётную запись ERP</strong><span className="mt-1 block text-xs text-slate-600">Карточки будут связаны автоматически. При увольнении доступ в ERP отключится.</span></span>
                        </label>
                        {form.create_erp_account ? <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <label className="block text-sm"><span className="font-semibold text-slate-700">Email / логин ERP *</span><input type="email" required className={`${inputClass} mt-1.5`} value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder="employee@company.ru" data-testid="employee-email" /></label>
                            <label className="block text-sm"><span className="font-semibold text-slate-700">Telegram</span><input className={`${inputClass} mt-1.5`} value={form.telegram_username} onChange={(e) => setField("telegram_username", e.target.value)} placeholder="@username" data-testid="employee-telegram" /></label>
                            <label className="block text-sm"><span className="font-semibold text-slate-700">Роль ERP</span><select className={`${inputClass} mt-1.5`} value={form.erp_role} onChange={(e) => setField("erp_role", e.target.value)} data-testid="employee-erp-role"><option value="dev">Сотрудник</option><option value="manager">Менеджер</option><option value="leader">Руководитель</option><option value="dept_leader">Руководитель отдела</option><option value="hr">HR</option></select></label>
                            <label className="block text-sm"><span className="font-semibold text-slate-700">Пароль</span><input type="password" className={`${inputClass} mt-1.5`} value={form.erp_password} onChange={(e) => setField("erp_password", e.target.value)} placeholder="Пусто — ERP сгенерирует" data-testid="employee-erp-password" /><span className={`mt-1 block text-xs ${passwordBytes(form.erp_password) > 72 ? "font-semibold text-red-600" : "text-slate-500"}`}>{passwordBytes(form.erp_password)}/72 байт</span></label>
                        </div> : <p className="mt-4 rounded-xl bg-white/80 px-4 py-3 text-xs text-slate-600">Будет создана только карточка HR-платформы. ERP-учётку можно связать позже фоновой синхронизацией по ФИО.</p>}
                    </section>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <label className="block text-sm">
                            <span className="font-semibold text-slate-700">Дата рождения</span>
                            <input
                                type="date"
                                className={`${inputClass} mt-1.5`}
                                value={form.birth_date}
                                onChange={(e) => setField("birth_date", e.target.value)}
                                data-testid="employee-birth-date"
                            />
                        </label>
                        <label className="flex items-center gap-2 self-end rounded-xl border border-slate-200 px-4 py-3 text-sm">
                            <input
                                type="checkbox"
                                checked={form.create_birthday_events}
                                disabled={!form.birth_date}
                                onChange={(e) => setField("create_birthday_events", e.target.checked)}
                                data-testid="employee-create-birthday-event"
                            />
                            Создать ежегодное событие в календаре
                        </label>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <label className="block text-sm">
                            <span className="font-semibold text-slate-700">Дата выхода на работу</span>
                            <input
                                type="date"
                                className={`${inputClass} mt-1.5`}
                                value={form.date_hired}
                                onChange={(e) => setField("date_hired", e.target.value)}
                                data-testid="employee-date-hired"
                            />
                        </label>
                        <label className="block text-sm">
                            <span className="font-semibold text-slate-700">Телефон</span>
                            <PhoneInput
                                className={`${inputClass} mt-1.5`}
                                value={form.phone_number}
                                onChange={(value) => setField("phone_number", value)}
                                data-testid="employee-phone"
                            />
                        </label>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <label className="block text-sm">
                            <span className="font-semibold text-slate-700">Пол</span>
                            <select
                                className={`${inputClass} mt-1.5`}
                                value={form.gender}
                                onChange={(e) => setField("gender", e.target.value)}
                            >
                                <option value="">Не указан</option>
                                <option value="male">Мужской</option>
                                <option value="female">Женский</option>
                            </select>
                        </label>
                        <label className="block text-sm">
                            <span className="font-semibold text-slate-700">Стаж (лет)</span>
                            <input
                                type="number"
                                min={0}
                                className={`${inputClass} mt-1.5`}
                                value={form.service_length}
                                onChange={(e) => setField("service_length", e.target.value)}
                            />
                        </label>
                    </div>

                    <label className="block text-sm">
                        <span className="font-semibold text-slate-700">Маршрут адаптации</span>
                        <select
                            className={`${inputClass} mt-1.5`}
                            value={form.adaptation_route}
                            onChange={(e) => setField("adaptation_route", e.target.value)}
                            data-testid="employee-adaptation-route"
                        >
                            <option value="">Определить автоматически по должности</option>
                            <option value="full">Полная адаптация: 1 неделя, 1 месяц, 2 месяца</option>
                            <option value="control">Контроль испытательного срока: 2 месяца</option>
                        </select>
                        <span className="block mt-1 text-xs text-slate-500">
                            HR может переопределить маршрут для конкретного сотрудника.
                        </span>
                    </label>
                </div>

                {error && (
                    <p className="mt-4 text-sm text-red-600" role="alert">
                        {error}
                    </p>
                )}

                <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-100">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isLoading}
                        className="px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50"
                    >
                        Отмена
                    </button>
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="px-5 py-2.5 text-sm font-medium text-white bg-slate-900 rounded-xl hover:bg-slate-800 disabled:opacity-50"
                        data-testid="employee-submit"
                    >
                        {isLoading ? "Сохранение…" : "Создать сотрудника"}
                    </button>
                </div>
            </form>
        </div>
    );
}
